
import React, { useState } from 'react';
import { X, BrainCircuit, Languages, Save } from 'lucide-react';
import { ProjectType } from '../../types';
import { useTourStore } from '../../store/useTourStore';
import { useProjectStore } from '../../store/useProjectStore';
import { createDemoProject, createDemoFile, DEMO_PROJECT_ID, DEMO_TERMS } from '../../utils/demoProject';

interface CreateProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (name: string, type: ProjectType) => void;
    mode?: ProjectType | 'select';
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ isOpen, onClose, onCreate, mode = 'select' }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState<ProjectType>(mode === 'select' ? 'translate' : mode);
    const tourStep = useTourStore(s => s.step);
    const tourAdvance = useTourStore(s => s.advance);
    const inTour = tourStep === 'create-modal';

    // Sync type if mode changes while open
    React.useEffect(() => {
        if (mode !== 'select') {
            setType(mode);
        }
    }, [mode]);

    // Khi tour mode active + modal mở → prefill name + force translate
    React.useEffect(() => {
        if (inTour && isOpen) {
            setName('Tài liệu của tôi');
            setType('translate');
        }
    }, [inTour, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (inTour) {
            // Tour mode: KHÔNG tạo project thật, ensure demo tồn tại + advance step
            const store = useProjectStore.getState();
            if (!store.projects.find(p => p.id === DEMO_PROJECT_ID)) {
                store.addProject(createDemoProject());
                store.addFile(createDemoFile());
            } else {
                // Patch demo project/file nếu thiếu data từ session cũ
                const demoProj = store.projects.find(p => p.id === DEMO_PROJECT_ID);
                if (demoProj && (!demoProj.importedTerminology || demoProj.importedTerminology.length === 0)) {
                    store.updateProject({ ...demoProj, synonymGroups: DEMO_TERMS, importedTerminology: DEMO_TERMS });
                }
                const demoFile = store.files.find(f => f.projectId === DEMO_PROJECT_ID);
                if (demoFile && !demoFile.estimatedCredits) {
                    store.updateFile(demoFile.id, { estimatedCredits: { translate: 420, qc: 126 } });
                }
            }
            tourAdvance('select-demo');
            setName('');
            onClose();
            return;
        }
        if (name.trim()) {
            onCreate(name.trim(), type);
            setName('');
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-300">
            <div data-tour="create-modal-card" className="bg-white w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-bold text-slate-800">
                        {mode === 'translate' ? 'Tạo dự án Dịch thuật' :
                            mode === 'train' ? 'Tạo dự án Huấn luyện' :
                                'Tạo dự án mới'}
                    </h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {inTour && (
                        <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-800 flex items-start gap-2">
                            <span className="inline-flex items-center justify-center w-5 h-5 bg-purple-600 text-white rounded-full text-[10px] font-black shrink-0">2</span>
                            <span><b>Bước 2/4 — Đặt tên dự án.</b> Tên đã điền sẵn cho bạn. Bấm <b>"Tạo dự án"</b> để dùng project demo có sẵn (không tạo mới).</span>
                        </div>
                    )}
                    {/* Project Type Selection - Only show if mode is 'select' */}
                    {mode === 'select' ? (
                        <div className="space-y-3">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Loại dự án</label>
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    type="button"
                                    onClick={() => setType('translate')}
                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${type === 'translate'
                                        ? 'border-indigo-500 bg-indigo-50 shadow-md ring-2 ring-indigo-500/20'
                                        : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                                        }`}
                                >
                                    <div className={`p-3 rounded-full ${type === 'translate' ? 'bg-indigo-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                        <Languages className="w-6 h-6" />
                                    </div>
                                    <div className="text-center">
                                        <div className={`font-bold text-sm ${type === 'translate' ? 'text-indigo-900' : 'text-slate-700'}`}>Dự án Dịch thuật</div>
                                        <div className="text-[10px] text-slate-500 mt-1">Dịch tài liệu sử dụng thuật ngữ đã nhập</div>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setType('train')}
                                    className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-3 ${type === 'train'
                                        ? 'border-amber-500 bg-amber-50 shadow-md ring-2 ring-amber-500/20'
                                        : 'border-slate-100 hover:border-slate-200 hover:bg-slate-50'
                                        }`}
                                >
                                    <div className={`p-3 rounded-full ${type === 'train' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                        <BrainCircuit className="w-6 h-6" />
                                    </div>
                                    <div className="text-center">
                                        <div className={`font-bold text-sm ${type === 'train' ? 'text-amber-900' : 'text-slate-700'}`}>Dự án Huấn luyện</div>
                                        <div className="text-[10px] text-slate-500 mt-1">Huấn luyện thuật ngữ với bản đối chiếu</div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className={`p-3 rounded-xl border ${type === 'translate' ? 'bg-indigo-50 border-indigo-100' : 'bg-amber-50 border-amber-100'} flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-500`}>
                            <div className={`p-2 rounded-lg ${type === 'translate' ? 'bg-indigo-500 text-white' : 'bg-amber-500 text-white'}`}>
                                {type === 'translate' ? <Languages className="w-4 h-4" /> : <BrainCircuit className="w-4 h-4" />}
                            </div>
                            <div className="text-xs">
                                <span className="font-bold text-slate-500 uppercase">Chế độ: </span>
                                <span className={`font-bold ${type === 'translate' ? 'text-indigo-700' : 'text-amber-700'}`}>
                                    {type === 'translate' ? 'Dịch thuật' : 'Huấn luyện'}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Project Name */}
                    <div className="space-y-2">
                        <label htmlFor="projectName" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tên dự án</label>
                        <input
                            id="projectName"
                            autoFocus
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="VD: Báo cáo thường niên 2025"
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none font-medium"
                            required
                        />
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
                        >
                            Hủy
                        </button>
                        <button
                            type="submit"
                            data-tour="modal-submit"
                            className={`flex-3 px-6 py-3 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 ${type === 'translate' ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100' : 'bg-amber-500 hover:bg-amber-600 shadow-amber-100'} ${inTour ? 'tour-pulse-button' : ''}`}
                        >
                            <Save className="w-5 h-5" />
                            Tạo dự án
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateProjectModal;
